(() => {
  const productHandleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (!productHandleMatch) return;

  const staticGalleryHandles = new Set([
    "zellenergie-system",
    "niucell-zellenergie-system-bundle",
    "nmn",
    "niucell-nmn",
    "nmn-optimizer",
    "niucell-nmn-optimizer",
  ]);
  if (staticGalleryHandles.has(productHandleMatch[1])) return;

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const imageFromProductJson = (image) => {
    if (typeof image === "string") return image;
    return image?.src || image?.url || "";
  };

  const growToCount = (items, parent, count) => {
    while (items.length < count && items[0]) {
      const clone = items[0].cloneNode(true);
      parent.append(clone);
      items.push(clone);
    }
  };

  const syncImage = (container, imageUrl, productTitle, index) => {
    const image = container?.querySelector("img");
    if (!image || !imageUrl) return;

    image.src = imageUrl;
    image.alt = productTitle || image.alt || "";
    image.loading = index === 0 ? "eager" : "lazy";
    image.fetchPriority = index === 0 ? "high" : "auto";
    image.removeAttribute("srcset");
    image.removeAttribute("sizes");
  };

  const setupGallery = (galleryWrap) => {
    const gallery = galleryWrap.querySelector("[data-product-gallery]");
    const track = gallery?.querySelector(".product-gallery-track");
    const realSlides = Array.from(gallery?.querySelectorAll(".product-gallery-slide") || []);
    const dots = Array.from(gallery?.querySelectorAll("[data-gallery-dot]") || []);
    const thumbs = Array.from(galleryWrap.querySelectorAll("[data-gallery-thumb]"));
    const prev = gallery?.querySelector("[data-gallery-prev]");
    const next = gallery?.querySelector("[data-gallery-next]");

    if (!track || realSlides.length === 0) return;

    const firstClone = realSlides[0].cloneNode(true);
    const lastClone = realSlides.at(-1).cloneNode(true);
    firstClone.dataset.galleryClone = "first";
    lastClone.dataset.galleryClone = "last";
    firstClone.setAttribute("aria-hidden", "true");
    lastClone.setAttribute("aria-hidden", "true");
    track.prepend(lastClone);
    track.append(firstClone);

    const slides = [lastClone, ...realSlides, firstClone];
    const realSlideCount = realSlides.length;
    let edgeResetTimer = 0;

    const getPhysicalIndex = () =>
      slides.reduce(
        (closest, slide, index) => {
          const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        },
        { index: 1, distance: Infinity },
      ).index;

    const getActiveIndex = () =>
      ((getPhysicalIndex() - 1) % realSlideCount + realSlideCount) % realSlideCount;

    const jumpToPhysicalSlide = (physicalIndex) => {
      track.classList.add("is-programmatic-scroll");
      track.scrollLeft = slides[physicalIndex].offsetLeft;
      requestAnimationFrame(() => track.classList.remove("is-programmatic-scroll"));
    };

    const resetEdgeClone = () => {
      const physicalIndex = getPhysicalIndex();
      if (physicalIndex === 0) jumpToPhysicalSlide(realSlideCount);
      if (physicalIndex === slides.length - 1) jumpToPhysicalSlide(1);
      updateState();
    };

    const updateState = () => {
      const activeIndex = getActiveIndex();

      dots.forEach((dot, index) => {
        dot.classList.toggle("is-active", index === activeIndex);
        dot.setAttribute("aria-current", index === activeIndex ? "true" : "false");
      });

      thumbs.forEach((thumb, index) => {
        thumb.classList.toggle("is-active", index === activeIndex);
        thumb.setAttribute("aria-current", index === activeIndex ? "true" : "false");
      });

      prev?.removeAttribute("disabled");
      next?.removeAttribute("disabled");
    };

    const goToPhysicalSlide = (physicalIndex) => {
      const boundedIndex = Math.max(0, Math.min(slides.length - 1, physicalIndex));
      window.clearTimeout(edgeResetTimer);
      track.scrollTo({
        left: slides[boundedIndex].offsetLeft,
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
      edgeResetTimer = window.setTimeout(resetEdgeClone, prefersReducedMotion ? 0 : 560);
    };

    const moveBy = (direction) => {
      const physicalIndex = getPhysicalIndex();
      const normalizedPhysicalIndex =
        physicalIndex === 0
          ? realSlideCount
          : physicalIndex === slides.length - 1
            ? 1
            : physicalIndex;
      if (normalizedPhysicalIndex !== physicalIndex) jumpToPhysicalSlide(normalizedPhysicalIndex);
      goToPhysicalSlide(normalizedPhysicalIndex + direction);
    };

    prev?.addEventListener("click", () => moveBy(-1));
    next?.addEventListener("click", () => moveBy(1));
    dots.forEach((dot, index) => dot.addEventListener("click", () => goToPhysicalSlide(index + 1)));
    thumbs.forEach((thumb, index) => thumb.addEventListener("click", () => goToPhysicalSlide(index + 1)));
    track.addEventListener("scroll", updateState, { passive: true });
    window.addEventListener(
      "resize",
      () => {
        jumpToPhysicalSlide(getActiveIndex() + 1);
        updateState();
      },
      { passive: true },
    );
    jumpToPhysicalSlide(1);
    updateState();
  };

  const hydrateGallery = async () => {
    const galleryWrap = Array.from(document.querySelectorAll(".product-gallery")).find(
      (gallery) => !gallery.hidden,
    );
    const gallery = galleryWrap?.querySelector("[data-product-gallery]");
    const track = gallery?.querySelector(".product-gallery-track");
    const dotsWrap = gallery?.querySelector(".product-gallery-dots");
    const thumbsWrap = galleryWrap?.querySelector(".product-gallery-grid");

    if (!galleryWrap || !gallery || !track || !dotsWrap || !thumbsWrap) return;

    const routeRoot = window.Shopify?.routes?.root || "/";
    const productUrl = `${routeRoot.replace(/\/?$/, "/")}products/${productHandleMatch[1]}.js`;
    const response = await fetch(productUrl, { headers: { Accept: "application/json" } });
    if (!response.ok) return;

    const productData = await response.json();
    const productImages = Array.isArray(productData.images)
      ? productData.images.map(imageFromProductJson).filter(Boolean)
      : [];

    if (productImages.length === 0) return;

    const nextGalleryWrap = galleryWrap.cloneNode(true);
    const nextGallery = nextGalleryWrap.querySelector("[data-product-gallery]");
    const nextTrack = nextGallery.querySelector(".product-gallery-track");
    const nextDotsWrap = nextGallery.querySelector(".product-gallery-dots");
    const nextThumbsWrap = nextGalleryWrap.querySelector(".product-gallery-grid");

    nextTrack.querySelectorAll("[data-gallery-clone]").forEach((clone) => clone.remove());

    let slides = Array.from(nextGallery.querySelectorAll(".product-gallery-slide"));
    let dots = Array.from(nextGallery.querySelectorAll("[data-gallery-dot]"));
    let thumbs = Array.from(nextGalleryWrap.querySelectorAll("[data-gallery-thumb]"));

    growToCount(slides, nextTrack, productImages.length);
    growToCount(dots, nextDotsWrap, productImages.length);
    growToCount(thumbs, nextThumbsWrap, productImages.length);

    slides.slice(productImages.length).forEach((slide) => slide.remove());
    dots.slice(productImages.length).forEach((dot) => dot.remove());
    thumbs.slice(productImages.length).forEach((thumb) => thumb.remove());

    slides = Array.from(nextGallery.querySelectorAll(".product-gallery-slide"));
    dots = Array.from(nextGallery.querySelectorAll("[data-gallery-dot]"));
    thumbs = Array.from(nextGalleryWrap.querySelectorAll("[data-gallery-thumb]"));

    slides.forEach((slide, index) => syncImage(slide, productImages[index], productData.title, index));
    thumbs.forEach((thumb, index) => {
      syncImage(thumb, productImages[index], productData.title, index);
      thumb.dataset.galleryThumb = String(index);
      thumb.setAttribute("aria-label", `Produktbild ${index + 1} anzeigen`);
      thumb.classList.toggle("is-active", index === 0);
      thumb.setAttribute("aria-current", index === 0 ? "true" : "false");
    });

    dots.forEach((dot, index) => {
      dot.dataset.galleryDot = String(index);
      dot.setAttribute("aria-label", `Produktbild ${index + 1} anzeigen`);
      dot.classList.toggle("is-active", index === 0);
      dot.setAttribute("aria-current", index === 0 ? "true" : "false");
    });

    galleryWrap.replaceWith(nextGalleryWrap);
    setupGallery(nextGalleryWrap);
  };

  hydrateGallery().catch(() => {});
})();
